import React, { useContext, useState as classicUseState } from 'react';
import ReactDom from 'react-dom';
import { State } from '@hookstate/core';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

import { useMutation, useQuery } from 'react-query';
import {
  EquipmentFieldNameDictionary,
  FieldConfigurationLocationOptions,
  IFieldConfiguration,
  MiscellaneousFieldNameDictionary,
  SoftwareFieldNameDictionary,
  StudioServiceFieldNameDictionary,
} from '@models/FieldConfiguration.model';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripVertical, faLock } from '@fortawesome/free-solid-svg-icons';
import { indexCustomField } from '@services/queries/customField';
import { updateOrderFieldConfiguration } from '@services/queries/fieldsConfiguration';
import Cancel from '@assets/svg/times.svg';
import Confirmation from '@assets/svg/check.svg';
import { AddField, EditableFieldConfig } from '@pages/Asset/components/Legacy';
import {
  EditableFieldButton,
  Icon,
} from '@pages/Asset/components/Legacy/ListInformation/components/ListInformationFields/components/EditableDateField/EditableDateField.styles';
import { trackInventoryFieldsConfiguration } from '@services/mixpanel/trackers/InventorySearchMetrics';
import { NavigationContext } from '@layout/navigation/navigation';
import {
  Container,
  Label,
  BlueIcon,
  Header,
  Buttons,
  RowDiv,
  Overlay,
  Bar,
  ConfigItem,
  FixedConfigItem,
  GrayIcon,
  TableHeader,
} from './ConfigProductHeader.styles';

interface IAddFieldContent {
  openAddField: State<boolean>;
  initialConfigData: IFieldConfiguration[];
  updateConfiguration: () => void;
  location: FieldConfigurationLocationOptions;
  similarLocation: FieldConfigurationLocationOptions;
  fieldNameDictionary:
    | StudioServiceFieldNameDictionary
    | MiscellaneousFieldNameDictionary
    | SoftwareFieldNameDictionary
    | EquipmentFieldNameDictionary;
}

export const PRODUCT_TABLE_CUSTOMFIELDS_KEY = 'customFields';

const ConfigProductTable = ({
  openAddField,
  initialConfigData,
  location,
  similarLocation,
  fieldNameDictionary,
  updateConfiguration,
}: IAddFieldContent) => {
  const portalDiv = document.getElementById('portal');
  const [configurationsListTemp, setConfigurationsList] = classicUseState<
    IFieldConfiguration[]
  >([]);

  const configurationsList = configurationsListTemp;

  const { userEmail, studioName } = useContext(NavigationContext);

  if (configurationsList.length === 0 && initialConfigData.length > 0) {
    setConfigurationsList(initialConfigData);
  }
  // Note: This query is also used to prefill onboarding customfields.
  const { data } = useQuery(PRODUCT_TABLE_CUSTOMFIELDS_KEY, indexCustomField);

  const mutationUpdateOrderFieldConfiguration = useMutation(
    updateOrderFieldConfiguration,
    {
      onSuccess: async () => {
        await updateConfiguration();
        await openAddField.set(false);

        trackInventoryFieldsConfiguration(
          userEmail,
          studioName,
          configurationsList.map(item => item.name),
        );
      },
      onError: () => {
        //  toast.error('Updated failed!');
      },
    },
  );

  const fields = Object.values(fieldNameDictionary).filter(
    field => !configurationsList.map(item => item.name).includes(field),
  );

  const customFields = data
    ? data
        .map(test => test.name)
        .filter(
          field =>
            !configurationsList
              .map(configName => configName.name)
              .includes(field),
        )
        .sort()
    : [];

  function handleOnDragEnd(result: any) {
    if (!result.destination) return;

    const items = Array.from(configurationsList);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setConfigurationsList(items);
  }

  const widthNumber = 70 / configurationsList.length;
  return openAddField.get() && portalDiv ? (
    ReactDom.createPortal(
      <Overlay>
        <Container>
          <Header>
            Preview
            <Buttons>
              <EditableFieldButton
                id="cancelButton"
                isHidden={false}
                onClick={() => {
                  openAddField.set(false);
                  setConfigurationsList(initialConfigData);
                }}
              >
                <Icon src={Cancel} isConfirm alt="cancel" />
              </EditableFieldButton>

              <EditableFieldButton
                id="confirmButton"
                data-testid="field-config-confirm"
                isConfirm
                isHidden={false}
                onClick={async () => {
                  const newTableView = configurationsList.map(
                    (fieldConfiguration, index) => {
                      if (fieldConfiguration.customField) {
                        return {
                          order: index,
                          customFieldId: fieldConfiguration.customField.id,
                        };
                      }
                      return {
                        order: index,
                        fieldName: fieldConfiguration.fieldName,
                      };
                    },
                  );
                  await mutationUpdateOrderFieldConfiguration.mutate({
                    location,
                    data: { updatedOrders: newTableView },
                  });
                }}
              >
                <Icon src={Confirmation} alt="confirm" />
              </EditableFieldButton>
            </Buttons>
          </Header>
          <Bar>
            {configurationsList.map(item => {
              return (
                <TableHeader maxWidth={widthNumber}>{item.name}</TableHeader>
              );
            })}
          </Bar>

          <DragDropContext onDragEnd={handleOnDragEnd}>
            <Droppable droppableId="characters" direction="horizontal">
              {(provided: any) => (
                <RowDiv {...provided.droppableProps} ref={provided.innerRef}>
                  {configurationsList.map((item, index) => {
                    return (
                      <Draggable
                        draggableId={item.id}
                        key={item.id}
                        index={index}
                      >
                        {(provided2: any) => (
                          <ConfigItem
                            ref={provided2.innerRef}
                            {...provided2.draggableProps}
                            {...provided2.dragHandleProps}
                            maxWidth={widthNumber}
                          >
                            <BlueIcon>
                              <FontAwesomeIcon icon={faGripVertical} />
                            </BlueIcon>
                            <EditableFieldConfig
                              fieldConfiguration={item}
                              index={index}
                              location={location}
                              options={fields.sort()}
                              optionsCustom={customFields}
                              customFields={data}
                              fieldsConfiguration={configurationsList}
                              updateFieldConfiguration={setConfigurationsList}
                              onClickTrash={() =>
                                setConfigurationsList(
                                  configurationsList.filter(
                                    config => config.id !== item.id,
                                  ),
                                )
                              }
                            />
                          </ConfigItem>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                  {configurationsList.length < 7 && (
                    <FixedConfigItem maxWidth={90}>
                      <AddField
                        index={configurationsList.length}
                        options={fields.sort()}
                        optionsCustom={customFields}
                        customFields={data}
                        fieldsConfiguration={configurationsList}
                        updateFieldConfiguration={setConfigurationsList}
                        location={similarLocation}
                      />
                    </FixedConfigItem>
                  )}
                  <FixedConfigItem maxWidth={widthNumber}>
                    <GrayIcon>
                      <FontAwesomeIcon icon={faLock} />
                    </GrayIcon>
                    <Label>Tags</Label>
                  </FixedConfigItem>
                </RowDiv>
              )}
            </Droppable>
          </DragDropContext>
        </Container>
      </Overlay>,
      portalDiv,
    )
  ) : (
    <></>
  );
};

export { ConfigProductTable };
